import axios, { AxiosResponse } from 'axios';
import Cookies from 'universal-cookie/es6';

import { IAssetDetails } from 'models/assets/i-asset-details.interface';
import { IAsset } from 'models/assets/i-asset.interface';
import { INonceResponse } from 'models/nonce-response.interface';
import { IProfileAsset, IProfileAssetPayload, IProfileTradeOffersPayload } from 'models/profile/i-profile-asset.interface';
import { IProfileAssetsFilter } from 'models/profile/i-profile-assets-filter.interface';
import { IProfileTrade, IProfileTradesPayload } from 'models/profile/i-profile-trade.interface';
import { IWalletCurrenciesResponse, IWalletCurrency } from 'models/wallet-currency-response.interface';
import { IWalletCurrencyDetails } from 'models/wallet-currency.interface';

import { CookiesKeys } from 'enums/cookies-keys.enum';

import {
  ASSET_DETAILS_FIELDS,
  ASSET_FIELDS,
  LANDING_FIELDS,
  PROFILE_FIELDS,
  PROFILE_TRADE_FIELDS,
  PROFILE_TRADE_OFFERS_FIELDS,
} from 'constants/params-fields.constant';

import { getIconByCurrencySymbol } from 'utils/get-currency-icon-by-symbol';
import { getQueryParams } from 'utils/get-query-params.utils';
import { removeAuthenticationCookies } from 'utils/remove-authentication-cookies.util';

import Web3Singleton from '../web3Singleton';
import { currencyBalanceContract } from './web3.service';

let cancelToken: any;

export const getCSRF = async (): Promise<AxiosResponse<any, any>> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  return axios.get<any>(`/api/v1/accounts/csrf/`);
};

export const getLogin = async (): Promise<string> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  return axios.get<any>(`/api/web3/login/`).then((loginResponse) => loginResponse.data.data);
};

export const postLogin = async (address: string, signature: string): Promise<AxiosResponse<any, any>> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  return axios.post<any>(`/api/web3/login/`, `address=${address}&signature=${signature}`, {
    headers: {
      'X-CSRFToken': new Cookies().get('csrftoken'),
    },
  });
};

export const postSignOut = async (): Promise<AxiosResponse<{}, any>> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  const cookie = new Cookies();

  cancelToken = axios.CancelToken.source();

  return axios
    .post<any>(`/api/v1/accounts/logout/`, null, {
      headers: {
        'X-CSRFToken': cookie.get(CookiesKeys.csrftoken),
      },
    })
    .then((data) => {
      const lgoWeb3 = Web3Singleton.getInstance();

      removeAuthenticationCookies();

      lgoWeb3.logout();

      return data;
    });
};

export const getAssets = async (
  params: IProfileAssetsFilter,
  signal: AbortSignal
): Promise<AxiosResponse<IProfileAssetPayload>> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  return axios.get<IProfileAssetPayload>(`/api/v1/assets/`, {
    signal,
    params: {
      ...params,
      page_size: 10,
      fields: [...ASSET_FIELDS, ...LANDING_FIELDS, ...PROFILE_FIELDS].join(','),
    },
    headers: {
      'X-CSRFToken': new Cookies().get('csrftoken'),
    },
  });
};

export const getOffersAssets = async (
  params: IProfileAssetsFilter,
  signal: AbortSignal
): Promise<AxiosResponse<IProfileTradeOffersPayload | null>> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  const offersActiveParams: IProfileAssetsFilter = {
    disable_aggregation: '',
    created_by_me: 'true',
    ...params,
  };

  const offersPayload = await axios.get<IProfileTradeOffersPayload>(`/api/v1/trades/offers/`, {
    signal,
    params: {
      ...offersActiveParams,
      page_size: 10,
      fields: PROFILE_TRADE_OFFERS_FIELDS.join(','),
    },
  });

  const noDataResponse = {
    count: 0,
    next: null,
    previous: null,
    results: [],
  };

  if (!offersPayload.data?.count) {
    return {
      ...offersPayload,
      data: noDataResponse,
    };
  }

  const assetIds = offersPayload.data.results.map(({ sale: { asset_id } }) => asset_id);
  const ids = [...new Set(assetIds)].join(',');

  const assetsPayload = await axios.get<IAsset[]>(`/api/v1/assets/all_assets`, {
    params: { ids, fields: ASSET_FIELDS.join(',') },
  });

  if (!assetsPayload?.data) {
    return {
      ...assetsPayload,
      data: noDataResponse,
    };
  }

  const assetMap = new Map(assetsPayload.data.map((a) => [a.id, a]));
  const mappedResults = offersPayload.data.results
    .filter((offer) => assetMap.has(offer.sale.asset_id))
    .map((offer) => ({
      ...offer,
      sale: {
        ...offer.sale,
        asset: assetMap.get(offer.sale.asset_id)!,
      },
    }));

  return {
    ...offersPayload,
    data: {
      ...offersPayload.data,
      results: mappedResults,
    },
  };
};

export const getListingAssets = async (
  params: IProfileAssetsFilter,
  signal: AbortSignal
): Promise<AxiosResponse<IProfileTradesPayload | null>> => {
  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  const listingActiveParams: IProfileAssetsFilter = {
    disable_aggregation: '',
    created_by_me: 'true',
    ...params,
  };

  const listingsPayload = await axios.get<IProfileTradesPayload>(`/api/v1/trades/`, {
    signal,
    params: { ...listingActiveParams, page_size: 10, fields: PROFILE_TRADE_FIELDS.join(',') },
  });

  const noDataResponse = {
    count: 0,
    next: null,
    previous: null,
    results: [],
  };

  if (!listingsPayload.data?.count) {
    return {
      ...listingsPayload,
      data: noDataResponse,
    };
  }

  const assetIds = listingsPayload.data.results.map(({ asset_id }) => asset_id);
  const ids = [...new Set(assetIds)].join(',');

  const assetsPayload = await axios.get<IAssetDetails[]>(`/api/v1/assets/all_assets`, {
    params: { ids, fields: [...ASSET_FIELDS, ...ASSET_DETAILS_FIELDS].join(',') },
  });

  if (!assetsPayload?.data?.length) {
    return {
      ...listingsPayload,
      data: noDataResponse,
    };
  }

  const assetMap = new Map(assetsPayload.data.map((a) => [a.id, a]));
  const mappedResults = listingsPayload.data.results
    .filter((trade) => assetMap.has(trade.asset_id))
    .map((trade) => ({
      ...trade,
      asset: assetMap.get(trade.asset_id)!,
    }));

  return {
    ...listingsPayload,
    data: {
      ...listingsPayload.data,
      results: mappedResults,
    },
  };
};

export const fetchCurrenciesService = async (): Promise<AxiosResponse<IWalletCurrency[]>> => {
  const currenciesResponse = await axios.get<IWalletCurrenciesResponse>(`/api/v1/assets/currencies/`, {
    params: getQueryParams({ page_size: '20' }),
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });

  return {
    ...currenciesResponse,
    data: await Promise.all(
      currenciesResponse.data.results.map(async (currencyResponse) => {
        return {
          ...currencyResponse,
          icon: getIconByCurrencySymbol(currencyResponse.symbol),
        };
      })
    ),
  };
};

export const fetchCurrenciesWithBalanceService = async (
  accountAddress: string
): Promise<AxiosResponse<IWalletCurrencyDetails[]>> => {
  const currenciesResponse = await axios.get<IWalletCurrenciesResponse>(`/api/v1/assets/currencies/`, {
    params: getQueryParams({ page_size: '20' }),
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });

  return {
    ...currenciesResponse,
    data: await Promise.all(
      currenciesResponse.data.results.map(async (currencyResponse) => {
        const balance = await currencyBalanceContract(currencyResponse.address, accountAddress);

        return {
          ...currencyResponse,
          icon: getIconByCurrencySymbol(currencyResponse.symbol),
          balance,
        };
      })
    ),
  };
};

export const fetchNonce = (): Promise<AxiosResponse<INonceResponse>> => {
  return axios.get(`/api/v1/accounts/nonce/`);
};

/**
 * Fetches the number of NFT shares owned by the authenticated user for a given token ID
 * @param tokenId - The token ID to query
 * @returns Promise<number> - The number of owned shares, or 0 if none found or on error
 * @throws Error if the request fails (caught and logged internally)
 */
export const getNftCountByTokenId = async (tokenId: string): Promise<number> => {
  if (!tokenId) {
    console.warn('getNftCountByTokenId called with empty tokenId');
    return 0;
  }

  try {
    const response = await axios.get<IProfileAssetPayload>(`/api/v1/assets/`, {
      params: {
        token_id: tokenId,
        page_size: 1,
      },
      headers: {
        'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
      },
    });

    if (response?.data?.results?.length > 0) {
      const ownedShares = response.data.results[0].owned_shares ?? 0;
      return ownedShares;
    }

    return 0;
  } catch (error) {
    console.error('Error fetching NFT count:', error);
    throw error;
  }
};

const MAX_PAGES = 200;

export const getAllCollectedAssets = async (
  params: Pick<IProfileAssetsFilter, 'search'>
): Promise<AxiosResponse<IProfileAssetPayload>> => {
  const PAGE_SIZE = 100;
  const baseParams = {
    owned_by_me: '1',
    ...params,
    page_size: PAGE_SIZE,
    fields: [...ASSET_FIELDS, ...LANDING_FIELDS, ...PROFILE_FIELDS].join(','),
  };
  const headers = { 'X-CSRFToken': new Cookies().get('csrftoken') };

  const firstResponse = await axios.get<IProfileAssetPayload>(`/api/v1/assets/`, {
    params: { ...baseParams, page: 1 },
    headers,
  });

  const totalPages = firstResponse.data.next
    ? Math.ceil(firstResponse.data.count / PAGE_SIZE)
    : 1;

  let allResults = [...firstResponse.data.results];

  if (totalPages > 1) {
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        axios.get<IProfileAssetPayload>(`/api/v1/assets/`, {
          params: { ...baseParams, page: i + 2 },
          headers,
        })
      )
    );
    for (const r of remaining) allResults = [...allResults, ...r.data.results];
  }

  return {
    ...firstResponse,
    data: { count: allResults.length, next: 0, previous: 0, results: allResults },
  };
};

export const getAllListingAssets = async (
  params: Pick<IProfileAssetsFilter, 'search' | 'show_status'>
): Promise<AxiosResponse<IProfileTradesPayload>> => {
  const PAGE_SIZE = 100;
  const baseParams = {
    disable_aggregation: '',
    created_by_me: 'true',
    ...params,
  };

  // ── Page 1: fetch results + total count ──
  const firstResponse = await axios.get<IProfileTradesPayload>(`/api/v1/trades/`, {
    params: { ...baseParams, page_size: PAGE_SIZE, page: 1, fields: PROFILE_TRADE_FIELDS.join(',') },
  });

  const totalPages = firstResponse.data.next
    ? Math.min(Math.ceil(firstResponse.data.count / PAGE_SIZE), MAX_PAGES)
    : 1;

  let allTradeStubs: Omit<IProfileTrade, 'asset'>[] = [...firstResponse.data.results];

  // ── Pages 2..N in parallel (matching getAllCollectedAssets pattern) ──
  if (totalPages > 1) {
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        axios.get<IProfileTradesPayload>(`/api/v1/trades/`, {
          params: { ...baseParams, page_size: PAGE_SIZE, page: i + 2, fields: PROFILE_TRADE_FIELDS.join(',') },
        })
      )
    );
    for (const r of remaining) {
      allTradeStubs = [...allTradeStubs, ...r.data.results];
    }
  }

  if (!allTradeStubs.length) {
    return { ...firstResponse, data: { count: 0, next: null, previous: null, results: [] } };
  }

  const assetIds = [...new Set(allTradeStubs.map(({ asset_id }) => asset_id))];
  const assetsPayload = await axios.get<IAssetDetails[]>(`/api/v1/assets/all_assets`, {
    params: { ids: assetIds.join(','), fields: [...ASSET_FIELDS, ...ASSET_DETAILS_FIELDS].join(',') },
  });

  const assetMap = new Map(assetsPayload.data?.map((a) => [a.id, a]) ?? []);
  const mappedResults: IProfileTrade[] = allTradeStubs
    .filter((trade) => assetMap.has(trade.asset_id))
    .map((trade) => ({
      ...trade,
      asset: assetMap.get(trade.asset_id)!,
    }));

  return {
    ...firstResponse,
    data: { count: mappedResults.length, next: null, previous: null, results: mappedResults },
  };
};

export const fetchAllUserOwnedShares = async (): Promise<Map<number, number>> => {
  const map = new Map<number, number>();
  const PAGE_SIZE = 100;

  const sharedHeaders = { 'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken) };

  // ── Page 1: fetch results + total count ──
  const firstResponse = await axios.get<IProfileAssetPayload>(`/api/v1/assets/`, {
    params: {
      owned_by_me: '1',
      page: 1,
      page_size: PAGE_SIZE,
      fields: 'token_id,owned_shares',
    },
    headers: sharedHeaders,
  });

  for (const asset of firstResponse.data.results) {
    map.set(Number(asset.token_id), asset.owned_shares ?? 0);
  }

  const totalPages = firstResponse.data.next
    ? Math.min(Math.ceil(firstResponse.data.count / PAGE_SIZE), MAX_PAGES)
    : 1;

  // ── Pages 2..N in parallel (matching getAllCollectedAssets pattern) ──
  if (totalPages > 1) {
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        axios.get<IProfileAssetPayload>(`/api/v1/assets/`, {
          params: {
            owned_by_me: '1',
            page: i + 2,
            page_size: PAGE_SIZE,
            fields: 'token_id,owned_shares',
          },
          headers: sharedHeaders,
        })
      )
    );
    for (const response of remaining) {
      for (const asset of response.data.results) {
        map.set(Number(asset.token_id), asset.owned_shares ?? 0);
      }
    }
  }

  return map;
};

export const getSoonFinishedAuction = (): Promise<IProfileTrade> => {
  return axios
    .get(
      `/api/v1/trades/?show_status=0&ordering=expiration_time&created_by_me=true&page=1&page_size=1&disable_aggregation=1`
    )
    .then(({ data }) => data.results?.[0]);
};
